import { filterByDays } from "../../../pages/KPIDashboard"
import { formatTimeRange, getHoursInDay, getClinicTimes } from "../../../utils/generalUtils"

/**
 * Calculates process metrics for the Process Overview component
 * @param {Array} historicalData - Array of historical data objects
 * @param {Object} settings - Application settings, including goals
 * @param {Object} selectedPeriod - Object containing start and end dates for selected period
 * @param {Date} selectedPeriod.startDate - Start date of selected period
 * @param {Date} selectedPeriod.endDate - End date of selected period
 * @returns {Object} - Object containing positive metrics and metrics requiring attention
 */
export const calculateProcessMetrics = (historicalData, roiMetrics, selectedDays, settings, selectedPeriod = null, logging) => {
  if (!historicalData || !historicalData.length) {
    logging.warn('No historical data available');
    return {
      positiveMetrics: [],
      attentionMetrics: []
    };
  }

  // Define baseline period as one year minus the liveDate
  const liveDate = settings?.liveDate || "2025-03-16";
  const baselineEnd = new Date(liveDate);
  const baselineStart = new Date(baselineEnd);
  baselineStart.setFullYear(baselineEnd.getFullYear() - 1);

  // Filter data for baseline period
  const baselineData = filterByDays(
    roiMetrics.filter(day => {
      const date = new Date(day.date);
      return date >= baselineStart && date <= baselineEnd;
    }),
    selectedDays
  );

  // Filter data for selected period (if provided)
  const selectedData = selectedPeriod ? 
    historicalData.filter(day => {
      const date = new Date(day.date);
      return date >= selectedPeriod.startDate && date <= selectedPeriod.endDate;
    }) : 
    historicalData;

  const goals = settings?.goals || [];
  const waitTimeGoal = goals.find(g => g.name === "Patient Wait Time Goal (min)")?.value || 15;
  const templateComplianceGoal = goals.find(g => g.name === "Template Compliance Goal (%)")?.value || 80;

  // Log baseline period
  logging.info('Baseline period:', {
    component: "calculateProcessMetrics",
    function: "calculateProcessMetrics",
    baselineStart: baselineStart.toISOString(),
    baselineEnd: baselineEnd.toISOString(),
    baselineDataLength: baselineData.length
  });

  // Log selected period data
  logging.info('Selected period data:', {
    component: "calculateProcessMetrics",
    function: "calculateProcessMetrics",
    selectedDataLength: selectedData.length,
    startDate: selectedPeriod?.startDate?.toISOString(),
    endDate: selectedPeriod?.endDate?.toISOString()
  });

  // Calculate metrics
  const positiveMetrics = [];
  const attentionMetrics = [];

  // Patient Volume Growth
  const calculateVolumeGrowth = () => {
    // Calculate total volumes for both periods
    const baselineDays = baselineData.length;
    const selectedDays = selectedData.length;

    // Count weekdays and Saturdays in baseline data
    const weekdayCount = baselineData.filter(day => {
      const date = new Date(day.date + 'T00:00:00-07:00'); // PST timezone
      return date.getDay() !== 6; // 6 is Saturday
    }).length;

    const saturdayCount = baselineData.filter(day => {
      const date = new Date(day.date + 'T00:00:00-07:00'); // PST timezone
      return date.getDay() === 6; // 6 is Saturday
    }).length;

    // Calculate baseline volume using 51.4 for weekdays and 20 for Saturdays
    const baselineVolume = ((weekdayCount + 1) * 51.4) + ((saturdayCount + 1) * 20);

    // const baselineVolume = baselineData.reduce((sum, day) => sum + (day.patient_volumes?.completed || 0), 0);
    const selectedVolume = selectedData.reduce((sum, day) => sum + (day.patient_volumes?.completed || 0), 0);

    const averageBaselineVolume = baselineDays > 0 ? (baselineVolume / baselineDays).toFixed(2) : 0;
    const averageSelectedVolume = selectedDays > 0 ? (selectedVolume / selectedDays).toFixed(2) : 0;

    logging.info('Volume growth calculation:', {
      component: "calculateProcessMetrics",
      function: "calculateVolumeGrowth",
      baselineDays,
      selectedDays,
      weekdayCount,
      saturdayCount,
      averageBaselineVolume,
      averageSelectedVolume
    });

    if (averageBaselineVolume > 0) {
      const growthPercent = ((averageSelectedVolume - averageBaselineVolume) / averageBaselineVolume * 100).toFixed(1);
      
      // Only return as a positive metric if growth is positive
      if (parseFloat(growthPercent) > 0) {
        return {
          title: "Completed Patient Volume Growth",
          value: `${growthPercent}%`,
          description: `Patient volume has increased from the baseline period (${baselineStart.toLocaleDateString()} to ${baselineEnd.toLocaleDateString()})`,
          rawValue: `${averageSelectedVolume}`,
          percentValue: parseFloat(growthPercent)
        };
      }
    }
    return null;
  };

  // Average Wait Time
  const calculateWaitTime = () => {
    const waitTimes = selectedData
      .filter(day => day.patient_wait_time?.averageWaitTimePerPatient !== undefined && day.patient_wait_time?.averageWaitTimePerPatient !== null)
      .map(day => day.patient_wait_time?.averageWaitTimePerPatient);

    if (waitTimes.length === 0) return null;

    const avgWaitTime = (waitTimes.reduce((sum, time) => sum + time, 0) / waitTimes.length).toFixed(1);
    const avgWaitTimeNum = parseFloat(avgWaitTime);

    const baselineWaitTimes = baselineData
      .filter(day => day.patient_wait_time?.averageWaitTimePerPatient !== undefined && day.patient_wait_time?.averageWaitTimePerPatient !== null)
      .map(day => day.patient_wait_time?.averageWaitTimePerPatient);

    const avgBaselineWaitTime = baselineWaitTimes.length > 0 
      ? (baselineWaitTimes.reduce((sum, time) => sum + time, 0) / baselineWaitTimes.length).toFixed(1)
      : null;

    const rawValue = avgBaselineWaitTime !== null 
      ? `${(((avgWaitTimeNum - parseFloat(avgBaselineWaitTime)) / parseFloat(avgBaselineWaitTime)) * 100).toFixed(1)}%  from baseline`
      : "N/A";
    
    if (avgWaitTimeNum < waitTimeGoal) {
      // Positive metric - below goal
    return {
      title: "Amazing Average Patient Wait Time",
      value: `${avgWaitTime} min`,
      description: `Efficient throughput: average wait time is below the goal of ${waitTimeGoal} min.`,
      rawValue: rawValue,
      goal: waitTimeGoal
    };
    } else if (avgWaitTimeNum >= waitTimeGoal && avgWaitTimeNum <= 30) {
      // Warning - above goal but under 30 min
      return {
        title: "Average Patient Wait Time Above Target",
        value: `${avgWaitTime} min`,
        description: `Above ${waitTimeGoal} min threshold`,
        rawValue: avgWaitTimeNum,
        goal: waitTimeGoal,
        status: "warning"
      };
    } else {
      // Critical - above 30 min
      return {
        title: "Average Patient Wait Time Critically High",
        value: `${avgWaitTime} min`,
        description: `Well above ${waitTimeGoal} min threshold`,
        rawValue: avgWaitTimeNum,
        goal: waitTimeGoal,
        status: "critical"
      };
    }
  };

  // Nursing Staff Underutilization
  const calculateNurseUnderutilization = () => {

    const genericDate = new Date('2025-05-26');
    
    // Get hours based on center settings
    const hours = getHoursInDay(settings, genericDate)
    const {openTime, closeTime} = getClinicTimes(settings, genericDate)
    const underUtilizationThreshold = settings.goals.find(goal => goal.name === "Nurse Capacity Goal (%)")?.value || 5;
    const hoursUnderUtilized = settings.goals.find(goal => goal.name === "Hours Under Utilized (%)")?.value || 5;
    
    // Create data structure for each hour
    const hourlyData = hours.map(minutesSinceMidnight => {
        const hour = Math.floor(minutesSinceMidnight / 60) // Convert minutes to hours
        return {
            hour,
            available: 0,
            processedDates: new Set()
        }
    })
    
    // Process each day's data
    selectedData.forEach(day => {
      if (day.nurse_workload?.hourly) {
        Object.entries(day.nurse_workload.hourly).forEach(([minuteKey, hourData]) => {
          // Convert minute key to hour (e.g., "480" -> 8)
          
          // Skip if the hour is outside operating hours
          if (minuteKey < openTime || minuteKey > closeTime) return;
          
          // Find the index for this hour
          const hourNum = minuteKey - openTime;


          const hourIndex = Math.floor(parseInt(hourNum) / 60);
          
          // Skip if hour index is invalid
          if (hourIndex < 0 || hourIndex >= hourlyData.length) return;

          // Get data for this hour
          const hourDataObj = hourlyData[hourIndex];
          
          // Track this date if not already processed for this hour
          if (!hourDataObj.processedDates.has(day.date)) {
            hourDataObj.processedDates.add(day.date);
          }
          
          // Add available nurses count
          hourDataObj.available += parseInt(
            hourData.availableNurses?.$numberInt || hourData.availableNurses || 0
          );
        });
      }
    });
    
    // Calculate averages and count hours with high availability
    let underutilizedHours = 0;
    
    // Convert to averages per day
    const hourlyAverages = hourlyData.map(hour => {
      const uniqueDaysCount = hour.processedDates.size || 1; // Avoid division by zero
      const avgAvailable = Math.round(hour.available / uniqueDaysCount);

      // Count as underutilized if average available nurses is ≥ 5
      if (avgAvailable >= underUtilizationThreshold && hour.hour >= 10) {
        underutilizedHours++;
      }
      
      return {
        hour: hour.hour,
        avgAvailable
      };
    });
    
    // Only create a warning if there are at least 3 underutilized hours
    if (underutilizedHours >= 3) {
      // Format the underutilized hours for display
      const underutilizedTimeRanges = [];
      let startHour = null;
      let endHour = null;
      
      hourlyAverages.forEach(hour => {
        if (hour.avgAvailable >= hoursUnderUtilized && hour.hour >= 10) {
          if (startHour === null) {
            startHour = hour.hour;
          }
          endHour = hour.hour;
        } else if (startHour !== null) {
          // We've reached the end of a range
          underutilizedTimeRanges.push(`${startHour}-${endHour+1}`);
          startHour = null;
          endHour = null;
        }
      });
      
      // Check if we have an open range at the end
      if (startHour !== null) {
        underutilizedTimeRanges.push(`${startHour}-${endHour+1}`);
      }
      
      const formattedRanges = underutilizedTimeRanges.map(formatTimeRange).join(', ');
      
    return {
        title: "Nursing Capacity Underutilized",
        value: `${underutilizedHours} hours`,
        description: formattedRanges ? `During: ${formattedRanges}` : "≥ 5 available nurses during these hours",
        status: "warning"
      };
    }
    
    return null;
  };

  // Nursing Staff Overutilization
  const calculateNurseOverutilization = () => {

    const genericDate = new Date('2025-05-26');
    // Define open and close hours
    const { openTime, closeTime } = getClinicTimes(settings, genericDate);
    
    // Get hours based on center settings
    const hours = getHoursInDay(settings, genericDate)
    
    // Create data structure for each hour
    const hourlyData = hours.map(minutesSinceMidnight => {
        const hour = Math.floor(minutesSinceMidnight / 60) // Convert minutes to hours
        return {
            hour,
            available: 0,
            overCapacity: 0,
            processedDates: new Set()
        }
    })
    
    // Process each day's data
    selectedData.forEach(day => {
      if (day.nurse_workload?.hourly) {
        Object.entries(day.nurse_workload.hourly).forEach(([minuteKey, hourData]) => {
          // Convert minute key to hour (e.g., "480" -> 8)
          
          // Skip if the hour is outside operating hours
          if (minuteKey < openTime || minuteKey > closeTime) return;
          
          // Find the index for this hour
          const hourNum = minuteKey - openTime;

          const hourIndex = Math.floor(parseInt(hourNum) / 60);
          
          // Skip if hour index is invalid
          if (hourIndex < 0 || hourIndex >= hourlyData.length) return;
          
          // Get data for this hour
          const hourDataObj = hourlyData[hourIndex];
          
          // Track this date if not already processed for this hour
          if (!hourDataObj.processedDates.has(day.date)) {
            hourDataObj.processedDates.add(day.date);
          }
          
          // Add available nurses count
          hourDataObj.available += parseInt(
            hourData.availableNurses?.$numberInt || hourData.availableNurses || 0
          );
          
          // Add overCapacity nurses count
          hourDataObj.overCapacity += parseInt(
            hourData.overCapacityNurses?.$numberInt || hourData.overCapacityNurses || 0
          );
        });
      }
    });
    
    // Calculate averages and count hours with overutilization
    let overutilizedHours = 0;
    
    // Convert to averages per day
    const hourlyAverages = hourlyData.map(hour => {
      const uniqueDaysCount = hour.processedDates.size || 1; // Avoid division by zero
      const avgAvailable = Math.round(hour.available / uniqueDaysCount);
      const avgOverCapacity = Math.round(hour.overCapacity / uniqueDaysCount);
      const overUtilizationThreshold = settings.goals.find(goal => goal.name === "Nurse Capacity Goal (%)")?.value || 3;
      
      // Count as overutilized if average overCapacity nurses is ≥ 1 AND available nurses is 0
      if (avgOverCapacity >= overUtilizationThreshold && avgAvailable === 0) {
        overutilizedHours++;
      }
      
      return {
        hour: hour.hour,
        avgAvailable,
        avgOverCapacity
      };
    });
    
    // Only create a critical alert if there are at least 2 overutilized hours
    if (overutilizedHours >= 2) {
      // Format the overutilized hours for display
      const overutilizedTimeRanges = [];
      let startHour = null;
      let endHour = null;
      
      hourlyAverages.forEach(hour => {
        if (hour.avgOverCapacity >= 1 && hour.avgAvailable === 0) {
          if (startHour === null) {
            startHour = hour.hour;
          }
          endHour = hour.hour;
        } else if (startHour !== null) {
          // We've reached the end of a range
          overutilizedTimeRanges.push(`${startHour}-${endHour+1}`);
          startHour = null;
          endHour = null;
        }
      });
      
      // Check if we have an open range at the end
      if (startHour !== null) {
        overutilizedTimeRanges.push(`${startHour}-${endHour+1}`);
      }
      
      const formattedRanges = overutilizedTimeRanges.map(formatTimeRange).join(', ');
      
    return {
        title: "Nurses are overloaded",
        value: `${overutilizedHours} hours`,
        description: formattedRanges ? `During: ${formattedRanges}` : "Nurses overCapacity with no backup",
        status: "critical"
      };
    }
    
    return null;
  };

  // Template Compliance
  const calculateTemplateCompliance = () => {
    const complianceData = selectedData
      .filter(day => day.template_compliance && day.template_compliance.percentage !== undefined)
      .map(day => day.template_compliance.percentage);

    if (complianceData.length === 0) return null;

    const avgCompliance = (complianceData.reduce((sum, val) => sum + val, 0) / complianceData.length).toFixed(1);
    const avgComplianceNum = parseFloat(avgCompliance);
    
    if (avgComplianceNum < 70) {
      // Red flag - critical issue
      return {
        title: "Low Template Compliance",
        value: `${avgCompliance}%`,
        description: `Template Compliance below ${templateComplianceGoal}% can cause unbalanced schedules`,
        percentValue: avgComplianceNum,
        goal: templateComplianceGoal,
        status: "critical"
      };
    } else if (avgComplianceNum < 80) {
      // Yellow flag - warning
    return {
      title: "Template compliance below target",
        value: `${avgCompliance}%`,
      description: `Target: ${templateComplianceGoal}%`,
        percentValue: avgComplianceNum,
        goal: templateComplianceGoal,
        status: "warning"
      };
    } else {
      // Green flag - good
      return {
        title: "Template compliance meets target",
        value: `${avgCompliance}%`,
        description: `Above minimum threshold`,
        percentValue: avgComplianceNum,
      goal: templateComplianceGoal
    };
    }
  };

  // Overtime
  const calculateOvertime = () => {
    const overtimeData = selectedData
      .filter(day => day.averageOvertimePerDay !== undefined && day.averageOvertimePerDay !== null)
      .map(day => day.averageOvertimePerDay);

    if (overtimeData.length === 0) return null;

    const avgOvertime = (overtimeData.reduce((sum, val) => sum + val, 0) / overtimeData.length).toFixed(1);
    const avgOvertimeNum = parseFloat(avgOvertime);
    
    if (avgOvertimeNum <= 0) {
      // Green - no overtime
      // return {
      //   title: "No staff overtime",
      //   value: "0 min",
      //   description: "Excellent time management",
      // };
    } else if (avgOvertimeNum < 30) {
      // Yellow - some overtime but acceptable
    return {
        title: "Staff overtime within limits",
        value: `${avgOvertime} min`,
        description: "Below 30 min threshold",
      status: "warning"
    };
    } else {
      // Red - excessive overtime
    return {
        title: "Excessive staff overtime",
        value: `${avgOvertime} min`,
        description: "Above 30 min threshold",
      status: "critical"
    };
    }
  };

  // Patient Punctuality
  const calculatePatientPunctuality = () => {
    let totalPatients = 0;
    let totalLatePatients = 0;
    
    selectedData.forEach(day => {
      if (day.patient_punctuality && day.patient_punctuality.counts) {
        const early = day.patient_punctuality.counts.early || 0;
        const onTime = day.patient_punctuality.counts.onTime || 0;
        const late = day.patient_punctuality.counts.late || 0;
        
        totalPatients += early + onTime + late;
        totalLatePatients += late;
      }
    });
    
    if (totalPatients === 0) return null;
    
    const latePercentage = (totalLatePatients / totalPatients * 100).toFixed(1);
    const latePercentageNum = parseFloat(latePercentage);

    // Calculate the raw value as latePercentage * 55
    const rawValue = `~${Math.floor(latePercentageNum * 0.55)} patients`;
    
    if (latePercentageNum > 10) {
      return {
          title: "Moderate Rate of Late Patients",
          value: `${latePercentage}%`,
          description: "Above 10% threshold",
          rawValue: rawValue,
          percentValue: latePercentageNum,
        status: "warning"
      };
    } else if (latePercentageNum > 25) {
      return {
          title: "High Rate of Late Patients",
          value: `${latePercentage}%`,
          description: "Above 25% threshold",
          rawValue: rawValue,
          percentValue: latePercentageNum,
        status: "critical"
      };
    } else {
      return {
          title: "Strong Patient Punctuality",
          value: `${latePercentage}% late`,
          description: "Less than 10% of patients are 30 minutes late",
          percentValue: latePercentageNum,
          rawValue: rawValue
        };
    }
  };

  // Patient-to-Nurse Ratio - High
  const calculateHighNurseRatio = () => {
    const ratioData = selectedData
      .filter(day => day.average_appointments_per_nurse !== undefined && day.average_appointments_per_nurse !== null)
      .map(day => day.average_appointments_per_nurse);

    if (ratioData.length === 0) return null;

    const avgRatio = (ratioData.reduce((sum, ratio) => sum + ratio, 0) / ratioData.length).toFixed(1);
    const avgRatioNum = parseFloat(avgRatio);
    
    // Only create a warning if the average ratio is above 5
    if (avgRatioNum >= 5) {
    return {
        title: "Overloaded Staff",
        value: `${avgRatio} patients/nurse`,
        description: "Patient-to-nurse ratio exceeds safe limits",
        status: "warning"
      };
    }
    
    return null;
  };

  // Patient-to-Nurse Ratio - Low
  const calculateLowNurseRatio = () => {
    const ratioData = selectedData
      .filter(day => day.average_appointments_per_nurse !== undefined && day.average_appointments_per_nurse !== null)
      .map(day => day.average_appointments_per_nurse);

    if (ratioData.length === 0) return null;

    const avgRatio = (ratioData.reduce((sum, ratio) => sum + ratio, 0) / ratioData.length).toFixed(1);
    const avgRatioNum = parseFloat(avgRatio);
    
    // Only create a warning if the average ratio is below 3
    if (avgRatioNum < 3) {
    return {
        title: "Underutilized Staff",
        value: `${avgRatio} patients/nurse`,
        description: "Patient-to-nurse ratio suggests potential overstaffing",
        status: "warning"
      };
    }
    
    return null;
  };

  // Add logging before metrics calculation
  logging.info('Starting metrics calculations');

  // Process calculations and sort metrics
  const volumeGrowth = calculateVolumeGrowth();
  const waitTime = calculateWaitTime();
  const nurseUnderutilization = calculateNurseUnderutilization();
  const nurseOverutilization = calculateNurseOverutilization();
  const templateCompliance = calculateTemplateCompliance();
  const overtime = calculateOvertime();
  const patientPunctuality = calculatePatientPunctuality();
  const highNurseRatio = calculateHighNurseRatio();
  const lowNurseRatio = calculateLowNurseRatio();

  // Add positive metrics
  if (volumeGrowth) {
    positiveMetrics.push(volumeGrowth);
  }

  if (waitTime && !waitTime.status) {
    positiveMetrics.push(waitTime);
  } else if (waitTime) {
    attentionMetrics.push(waitTime);
  }

  if (templateCompliance && !templateCompliance.status) {
    positiveMetrics.push(templateCompliance);
  } else if (templateCompliance) {
    attentionMetrics.push(templateCompliance);
  }

  if (overtime && !overtime.status) {
    positiveMetrics.push(overtime);
  } else if (overtime) {
    attentionMetrics.push(overtime);
  }

  if (patientPunctuality && !patientPunctuality.status) {
    positiveMetrics.push(patientPunctuality);
  } else if (patientPunctuality) {
    attentionMetrics.push(patientPunctuality);
  }

  // Add nurse utilization metrics individually
  if (nurseUnderutilization) {
    attentionMetrics.push(nurseUnderutilization);
  }

  if (nurseOverutilization) {
    attentionMetrics.push(nurseOverutilization);
  }

  if (highNurseRatio) {
    attentionMetrics.push(highNurseRatio);
  }

  if (lowNurseRatio) {
    attentionMetrics.push(lowNurseRatio);
  }

  // Log final results
  logging.info('Metrics calculation results:', {
    hasVolumeGrowth: !!volumeGrowth,
    hasWaitTime: !!waitTime,
    positiveMetricsCount: positiveMetrics.length,
    attentionMetricsCount: attentionMetrics.length
  });

  return {
    positiveMetrics: positiveMetrics.slice(0, 5), // Limit to 5 positive metrics for cleaner display
    attentionMetrics
  };
};

export default calculateProcessMetrics; 